import React, { useState, useRef, useLayoutEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LEGAL_TERMS_EN, LEGAL_TERMS_EL } from "../lib/legal";
import { Check, ChevronRight, ChevronLeft, Upload, Trash2, Eraser, IdCard } from "lucide-react";
import LocationSelect from "../lib/locationSelect";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ["About You", "Socials", "Identity", "Legal"];

export default function CollabForm() {
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState({
    full_name: "", legal_name: "", email: "", phone: "", date_of_birth: "",
    address: "", country: "", state: "", city: "", pronouns: "",
    twitter_handle: "", instagram_handle: "", onlyfans_link: "", justforfans_link: "", other_links: "",
    agreed_terms: false, signature_name: "", locale: "en",
    // ID photos (Cloudinary secure URLs)
    // ID photos are stored privately: the server returns an upload token, never a URL.
    id_front_upload: "", id_back_upload: "", selfie_upload: "",
    // Drawn signature (data URL PNG)
    signature_drawn_data_url: "",
    // Legacy Stripe Identity (kept optional for backwards compat)
    identity_session_id: "", identity_verified: false,
    website: "",  // honeypot
    // Platform consents
    consent_onlyfans: false,
    consent_rawfuckclub: false,
    consent_faphouse: false,
    consent_giopilos: false,
    face_visibility: "",  // "yes" or "no"
  });

  const up = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setFormError("");
  };
  // Local, in-browser previews of the private ID uploads (object URLs; never sent anywhere).
  const [idPreviews, setIdPreviews] = useState({});
  const setIdPreview = (slot, preview) => setIdPreviews((p) => {
    if (p[slot]) URL.revokeObjectURL(p[slot]);
    return { ...p, [slot]: preview || "" };
  });
  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    const validationError = !form.full_name || !form.email ? "Name and email required"
      : !form.legal_name.trim() ? "Legal name is required"
      : !form.date_of_birth ? "Date of birth is required"
      : !form.phone.trim() ? "Phone number is required"
      : !form.address.trim() ? "Address is required"
      : !form.country.trim() || !form.city.trim() ? "Country and city are required"
      : !form.twitter_handle.trim() ? "X / Twitter handle is required"
      : !form.id_front_upload || !form.id_back_upload || !form.selfie_upload ? "All 3 ID photos are required (Front, Back, Selfie)"
      : !form.agreed_terms ? "You must agree to the terms"
      : !form.signature_name.trim() ? "Type your full legal name to sign"
      : !form.signature_drawn_data_url ? "Please draw your signature"
      : "";
    if (validationError) {
      setFormError(validationError);
      toast.error(validationError);
      return;
    }
    setFormError("");
    setBusy(true);
    try {
      await axios.post(`${API}/collab-submissions/`, form);
      setSubmitted(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const message = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map(issue => issue.msg).filter(Boolean).join(" · ") : "Failed to submit";
      setFormError(message);
      toast.error(message);
    } finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <div className="collab-form min-h-screen text-white flex items-center justify-center p-4 sm:p-8" data-testid="collab-submitted" style={{ background: "var(--gio-bg)" }}>
        <div className="glass-panel max-w-lg w-full text-center fade-up p-8 sm:p-10">
          <div className="overline text-[color:var(--gio-primary)] mb-4">SUBMITTED · ΕΣΤΑΛΗ</div>
          <h1 className="font-heading text-5xl mb-4">Thank you.</h1>
          <p className="text-zinc-400 text-sm mb-8">
            Your application has landed in Gio&apos;s inbox. He&apos;ll review personally and reach out at{" "}
            <span className="text-white">{form.email}</span> if there&apos;s a fit.
          </p>
          <a href="https://linktr.ee/giopilos" className="btn-primary inline-block" data-testid="collab-back-linktree">
            Back to linktr.ee/giopilos →
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="collab-form min-h-screen text-white relative overflow-hidden" data-testid="collab-form-page" style={{ background: "var(--gio-bg)" }}>
      <div className="grain-overlay" />
      <div className="max-w-4xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-10 md:py-12 relative z-10">
        <header className="glass-panel p-5 sm:p-7 md:p-8 mb-6 sm:mb-8 fade-up" style={{ background: "var(--gio-surface)", borderColor: "var(--gio-border)" }}>
          <div className="flex items-center gap-4 mb-4">
            <img src="/gp-logo.png" alt="GP · Gio Pilos"
              data-testid="cf-logo"
              className="w-14 h-14 sm:w-16 sm:h-16 rounded-[var(--gio-radius)] object-contain shadow-sm"
              style={{ background: "var(--gio-surface-2)", border: "1px solid var(--gio-border)" }} />
            <div>
              <div className="overline text-[color:var(--gio-primary)] mb-1">GIOPILOS.COM · COLLAB INTAKE</div>
              <div className="font-mono-ui text-[11px] text-zinc-500">Athens, Greece · gio@giopilos.eu</div>
            </div>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl leading-tight">Model Release &amp;<br/><span className="italic" style={{ color: "var(--gio-muted)" }}>Content Collaboration.</span></h1>
          <p className="mt-4 text-sm max-w-xl" style={{ color: "var(--gio-muted)" }}>Complete all 4 steps to submit your application. Your info is reviewed personally by Gio.</p>
          
          {/* Live Platform Notice */}
          <div className="mt-6 p-4 border rounded-[var(--gio-radius)]" style={{ borderColor: "var(--gio-warning)", background: "var(--gio-surface-2)" }}>
            <p className="text-sm" style={{ color: "var(--gio-text-2)" }}>
              <strong>Note:</strong> giopilos.com is a live, published platform. Any content or profile information submitted here may be published across giopilos.com and its affiliated platforms (OnlyFans, JustForFans, FapHouse, RawFuckClub) according to the consents you select below.
            </p>
          </div>
        </header>

        {/* Stepper */}
        <div className="glass-panel flex items-center gap-2 sm:gap-4 mb-5 p-3 sm:p-4 overflow-x-auto" style={{ background: "var(--gio-surface)", borderColor: "var(--gio-border)" }} data-testid="collab-stepper">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-2 font-mono-ui text-[11px] uppercase tracking-wider whitespace-nowrap ${i === step ? "text-[color:var(--gio-primary)]" : i < step ? "text-white" : "text-zinc-600"}`} data-testid={`step-${i}`} aria-current={i === step ? "step" : undefined}>
                <span className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center border ${i === step ? "border-[color:var(--gio-primary)] bg-[color:var(--gio-primary-soft)]" : i < step ? "border-[color:var(--gio-border-hi)]" : "border-zinc-700"}`}>
                  {i < step ? <Check size={14} /> : (i + 1).toString()}
                </span>
                <span className="hidden sm:inline">{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 min-w-[20px] h-px ${i < step ? "bg-white" : "bg-zinc-800"}`} />}
            </React.Fragment>
          ))}
        </div>

        {formError && (
          <div role="alert" aria-live="assertive" data-testid="cf-form-error" className="mb-5 rounded-[var(--gio-radius)] border px-4 py-3 text-sm" style={{ color: "var(--gio-danger)", borderColor: "var(--gio-danger)", background: "var(--gio-surface)" }}>
            {formError}
          </div>
        )}

        <div className="glass-panel p-4 sm:p-6 md:p-8" style={{ background: "var(--gio-surface)", borderColor: "var(--gio-border)", boxShadow: "var(--gio-shadow)" }}>
        <div className="mb-6 sm:mb-8 border-b pb-4" style={{ borderColor: "var(--gio-border)" }}>
          <div className="overline" style={{ color: "var(--gio-primary)" }}>STEP {step + 1} OF {STEPS.length}</div>
          <h2 className="font-heading text-2xl sm:text-3xl mt-1">{STEPS[step]}</h2>
          <p className="text-sm mt-1" style={{ color: "var(--gio-muted)" }}>
            {step === 0 && "Start with the contact and identity details needed for your application."}
            {step === 1 && "Add your social profiles and creator links. These help us find and credit your work."}
            {step === 2 && "Upload the documents required to verify age and identity."}
            {step === 3 && "Review the terms, choose publishing permissions, and sign your application."}
          </p>
        </div>

        {/* Step 1 — About You */}
        {step === 0 && (
          <section className="space-y-4 fade-up">
            <div className="grid sm:grid-cols-2 gap-4">
              <F l="Full Name (Stage) *"><input data-testid="cf-name" value={form.full_name} onChange={(e) => up("full_name", e.target.value)} className="gf-input" /></F>
              <F l="Legal Name *"><input data-testid="cf-legal-name" value={form.legal_name} onChange={(e) => up("legal_name", e.target.value)} placeholder="As on government ID" className="gf-input" /></F>
              <F l="Email *"><input data-testid="cf-email" type="email" value={form.email} onChange={(e) => up("email", e.target.value)} className="gf-input" /></F>
              <F l="Phone *"><input data-testid="cf-phone" value={form.phone} onChange={(e) => up("phone", e.target.value)} placeholder="+30 ..." className="gf-input" /></F>
              <F l="Date of Birth (Age) *"><input data-testid="cf-dob" type="date" value={form.date_of_birth} onChange={(e) => up("date_of_birth", e.target.value)} className="gf-input" /></F>
              <F l="Pronouns"><input data-testid="cf-pronouns" value={form.pronouns} onChange={(e) => up("pronouns", e.target.value)} placeholder="he/him, she/her, they/them..." className="gf-input" /></F>
              <F l="Address *" full><input data-testid="cf-address" value={form.address} onChange={(e) => up("address", e.target.value)} placeholder="Street, building, postal code" className="gf-input" /></F>
              <LocationSelect
                country={form.country}
                state={form.state}
                city={form.city}
                onChange={({ country, state, city }) => { setForm(f => ({ ...f, country, state, city })); setFormError(""); }}
                testidPrefix="cf"
              />
            </div>
          </section>
        )}

        {/* Step 2 — Socials */}
        {step === 1 && (
          <section className="space-y-4 fade-up">
            <F l="Twitter / X Handle *"><input data-testid="cf-twitter" value={form.twitter_handle} onChange={(e) => up("twitter_handle", e.target.value)} placeholder="username" className="gf-input" /></F>
            <F l="Instagram Handle"><input data-testid="cf-instagram" value={form.instagram_handle} onChange={(e) => up("instagram_handle", e.target.value)} placeholder="username" className="gf-input" /></F>
            <F l="OnlyFans Link"><input data-testid="cf-onlyfans" value={form.onlyfans_link} onChange={(e) => up("onlyfans_link", e.target.value)} placeholder="onlyfans.com/..." className="gf-input" /></F>
            <F l="JustForFans Link"><input data-testid="cf-justforfans" value={form.justforfans_link} onChange={(e) => up("justforfans_link", e.target.value)} placeholder="justfor.fans/..." className="gf-input" /></F>
            <F l="Other Links"><textarea data-testid="cf-other" value={form.other_links} onChange={(e) => up("other_links", e.target.value)} rows={3} placeholder="Other platforms or relevant links" className="gf-input" /></F>
          </section>
        )}

        {/* Step 3 — Identity (ID photos) */}
        {step === 2 && (
          <section className="space-y-4 fade-up">
            <div className="overline text-[color:var(--gio-primary)] flex items-center gap-2"><IdCard size={14} /> IDENTITY DOCUMENTS · REQUIRED</div>
              <p className="text-sm" style={{ color: "var(--gio-muted)" }}>Upload clear, well-lit photos of your government-issued ID and a selfie holding it. Files are encrypted and used solely to verify your age (18+) and identity for legal compliance. <span style={{ color: "var(--gio-muted-2)" }}>Max 12 MB · JPG / PNG / HEIC.</span></p>
            <div className="grid sm:grid-cols-3 gap-4">
              <IdSlot
                testid="cf-id-front"
                label="ID — Front Side *"
                hint="Clearly readable name, photo & DOB"
                url={idPreviews.id_front || ""}
                onUpload={(u) => { up("id_front_upload", u.upload_token); setIdPreview("id_front", u.preview); }}
                onClear={() => { up("id_front_upload", ""); setIdPreview("id_front", ""); }}
              />
              <IdSlot
                testid="cf-id-back"
                label="ID — Back Side *"
                hint="Any signature / serial visible"
                url={idPreviews.id_back || ""}
                onUpload={(u) => { up("id_back_upload", u.upload_token); setIdPreview("id_back", u.preview); }}
                onClear={() => { up("id_back_upload", ""); setIdPreview("id_back", ""); }}
              />
              <IdSlot
                testid="cf-selfie"
                label="Selfie + ID *"
                hint="You holding the ID next to your face"
                url={idPreviews.selfie || ""}
                onUpload={(u) => { up("selfie_upload", u.upload_token); setIdPreview("selfie", u.preview); }}
                onClear={() => { up("selfie_upload", ""); setIdPreview("selfie", ""); }}
              />
            </div>
            <div className="text-[11px] border p-3 mt-2 rounded-[var(--gio-radius)]" style={{ color: "var(--gio-muted)", borderColor: "var(--gio-border)", background: "var(--gio-surface-2)" }}>
              Files stored privately on Cloudinary under <span className="text-zinc-300 font-mono-ui">collab-id-photos/</span>.
              They are embedded into the signed PDF contract that Gio downloads from the admin dashboard.
            </div>
          </section>
        )}

        {/* Step 4 — Legal */}
        {step === 3 && (
          <section className="space-y-6 fade-up">
            <div className="overline text-[color:var(--gio-primary)]">AGREEMENT TERMS · EN / ΕΛΛ</div>
            <div className="border border-zinc-800 bg-zinc-950 p-4 text-xs">
              <div className="font-mono-ui uppercase tracking-wider text-zinc-400 mb-3">HEADER · PARTICULARS</div>
              <div className="text-zinc-300 space-y-1">
                <div>Creator/Co-Model Legal Name: <span className="text-white">GEORGIOS STASINOPOULOS</span></div>
                <div>Creator/Co-Model Stage Name: <span className="text-white">GIO PILOS</span></div>
                <div>Creator/Co-Model Email: <span className="text-white">gio@giopilos.eu</span></div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 border border-zinc-800 max-h-[500px] overflow-y-auto" data-testid="cf-terms-scroller">
              <div className="p-5 border-r border-zinc-900">
                <div className="overline mb-3 sticky top-0 bg-zinc-950 py-2">ENGLISH</div>
                <ol className="space-y-4 text-[13px] text-zinc-300 leading-relaxed">
                  {LEGAL_TERMS_EN.map((t, i) => (
                    <li key={i}>
                      <div className="text-white font-medium">{i + 1}. {t.title}</div>
                      <p className="text-zinc-400 mt-1 whitespace-pre-wrap">{t.body}</p>
                    </li>
                  ))}
                </ol>
              </div>
              <div className="p-5">
                <div className="overline mb-3 sticky top-0 bg-zinc-950 py-2">ΕΛΛΗΝΙΚΑ</div>
                <ol className="space-y-4 text-[13px] text-zinc-300 leading-relaxed">
                  {LEGAL_TERMS_EL.map((t, i) => (
                    <li key={i}>
                      <div className="text-white font-medium">{i + 1}. {t.title}</div>
                      <p className="text-zinc-400 mt-1 whitespace-pre-wrap">{t.body}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            {/* Platform Consents */}
            <div className="space-y-4 border p-5 sm:p-6 rounded-[var(--gio-radius-lg)]" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface-2)" }}>
              <div className="overline text-[color:var(--gio-primary)] mb-4">PLATFORM PUBLISHING CONSENTS *</div>
              <p className="text-xs text-zinc-500 mb-4">Select which platforms your content may be published on:</p>
              
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={form.consent_onlyfans}
                  onChange={(e) => up("consent_onlyfans", e.target.checked)}
                  className="mt-1"
                />
                <span className="text-sm text-zinc-300 group-hover:text-white transition">
                  I agree to have my content published on <strong className="text-white">OnlyFans / JustForFans</strong>
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={form.consent_rawfuckclub}
                  onChange={(e) => up("consent_rawfuckclub", e.target.checked)}
                  className="mt-1"
                />
                <span className="text-sm text-zinc-300 group-hover:text-white transition">
                  I agree to be featured on <strong className="text-white">RawFuckClub</strong>
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={form.consent_faphouse}
                  onChange={(e) => up("consent_faphouse", e.target.checked)}
                  className="mt-1"
                />
                <span className="text-sm text-zinc-300 group-hover:text-white transition">
                  I agree to be featured on <strong className="text-white">FapHouse</strong>
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={form.consent_giopilos}
                  onChange={(e) => up("consent_giopilos", e.target.checked)}
                  className="mt-1"
                />
                <span className="text-sm text-zinc-300 group-hover:text-white transition">
                  I agree to be featured on <strong className="text-white">giopilos.com</strong>
                </span>
              </label>
            </div>

            {/* Face Visibility */}
            <div className="space-y-4 border p-5 sm:p-6 rounded-[var(--gio-radius-lg)]" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface-2)" }}>
              <div className="overline text-[color:var(--gio-primary)] mb-4">FACE VISIBILITY CONSENT *</div>
              <p className="text-xs text-zinc-500 mb-4">May your face be shown/visible in published content?</p>
              
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="radio"
                  name="face_visibility"
                  value="yes"
                  checked={form.face_visibility === "yes"}
                  onChange={(e) => up("face_visibility", e.target.value)}
                  className=""
                />
                <span className="text-sm text-zinc-300 group-hover:text-white transition">
                  <strong className="text-white">Yes</strong> — My face may be shown/visible in published content
                </span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="radio"
                  name="face_visibility"
                  value="no"
                  checked={form.face_visibility === "no"}
                  onChange={(e) => up("face_visibility", e.target.value)}
                  className=""
                />
                <span className="text-sm text-zinc-300 group-hover:text-white transition">
                  <strong className="text-white">No</strong> — My face must be blurred/cropped in published content
                </span>
              </label>
            </div>

            <div className="space-y-4 pt-2">
              <label className="flex items-start gap-3 cursor-pointer text-sm" data-testid="cf-agree-label">
                <input type="checkbox" checked={form.agreed_terms} onChange={(e) => up("agreed_terms", e.target.checked)} data-testid="cf-agreed" className="accent-[color:var(--gio-primary)] mt-1" />
                <span className="text-zinc-300">I have read, understood, and agree to all terms above (in English and/or Greek) — I am 18+ and have full legal capacity to sign.</span>
              </label>

              <F l="Type your full legal name as signature *">
                <input data-testid="cf-signature" value={form.signature_name} onChange={(e) => up("signature_name", e.target.value)} placeholder="GEORGIOS PAPADOPOULOS" className="gf-input" />
              </F>

              {/* Drawn signature canvas */}
              <SignaturePad
                value={form.signature_drawn_data_url}
                onChange={(v) => up("signature_drawn_data_url", v)}
              />

              <div className="flex gap-2 items-center">
                <span className="overline">SIGN IN</span>
                <button onClick={() => up("locale", "en")} data-testid="cf-lang-en" className={`px-3 py-1 text-[11px] font-mono-ui uppercase border ${form.locale === "en" ? "border-[color:var(--gio-primary)] text-[color:var(--gio-primary)]" : "border-zinc-800 text-zinc-400"}`}>EN</button>
                <button onClick={() => up("locale", "el")} data-testid="cf-lang-el" className={`px-3 py-1 text-[11px] font-mono-ui uppercase border ${form.locale === "el" ? "border-[color:var(--gio-primary)] text-[color:var(--gio-primary)]" : "border-zinc-800 text-zinc-400"}`}>ΕΛΛ</button>
              </div>

              {/* Honeypot */}
              <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => up("website", e.target.value)}
                style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }} aria-hidden="true" />
            </div>
          </section>
        )}

        </div>

        {/* Nav */}
        <div className="flex gap-3 mt-10">
          {step > 0 && <button onClick={back} className="btn-ghost inline-flex items-center gap-2" data-testid="cf-back-button"><ChevronLeft size={16} /> Back</button>}
          {step < STEPS.length - 1 && <button onClick={next} className="btn-primary inline-flex items-center gap-2 ml-auto" data-testid="cf-next-button">Next <ChevronRight size={16} /></button>}
          {step === STEPS.length - 1 && (
            <button onClick={submit} disabled={busy || !form.agreed_terms || !form.signature_name.trim() || !form.signature_drawn_data_url || !form.id_front_upload || !form.id_back_upload || !form.selfie_upload}
              className="btn-primary inline-flex items-center gap-2 ml-auto disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="cf-submit-button"
              title={!form.id_front_upload || !form.id_back_upload || !form.selfie_upload ? "Upload all 3 ID photos first" : !form.agreed_terms ? "Agree to the terms first" : !form.signature_name.trim() ? "Type your signature first" : !form.signature_drawn_data_url ? "Draw your signature first" : ""}
            >
              {busy ? "Submitting..." : "Submit Application →"}
            </button>
          )}
        </div>
      </div>
      <style>{`
        .collab-form .gf-input { width: 100%; background: var(--gio-surface-2); border: 1px solid var(--gio-border); color: var(--gio-text); border-radius: var(--gio-radius); padding: 11px 14px; font-size: 14px; outline: none; font-family: 'Manrope', sans-serif; transition: border-color 0.15s, box-shadow 0.15s; }
        .collab-form .gf-input::placeholder { color: var(--gio-muted-2); }
        .collab-form .gf-input:focus { border-color: var(--gio-primary); box-shadow: 0 0 0 3px var(--gio-primary-soft); }
        .collab-form input[type="checkbox"], .collab-form input[type="radio"] { accent-color: var(--gio-primary); }
        .collab-form :focus-visible { outline: 2px solid var(--gio-primary); outline-offset: 2px; }
        .collab-form button:focus-visible, .collab-form a:focus-visible { border-radius: var(--gio-radius); }
      `}</style>
    </div>
  );
}

function F({ l, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="text-sm font-semibold mb-1.5" style={{ color: "var(--gio-text-2)" }}>{l}</div>
      {children}
    </div>
  );
}

// ─────────────────── ID Photo upload slot ───────────────────
function IdSlot({ testid, label, hint, url, onUpload, onClear }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => inputRef.current?.click();
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Image files only"); return; }
    if (f.size > 12 * 1024 * 1024) { toast.error("Max 12 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await axios.post(`${API}/collab-submissions/upload-id-photo`, fd);
      if (!r.data?.upload_token) throw new Error("Upload failed");
      onUpload({ upload_token: r.data.upload_token, preview: URL.createObjectURL(f) });
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="border p-3 sm:p-4 rounded-[var(--gio-radius)]" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface-2)" }} data-testid={testid}>
      <div className="text-sm font-semibold mb-2" style={{ color: "var(--gio-text-2)" }}>{label}</div>
      {url ? (
        <div className="relative group">
          <img src={url} alt={label} className="w-full aspect-[3/4] object-cover border rounded-[var(--gio-radius)]" style={{ borderColor: "var(--gio-border)" }} data-testid={`${testid}-preview`} />
          <button onClick={onClear} data-testid={`${testid}-clear`}
            className="absolute top-2 right-2 border p-1.5 transition rounded-[var(--gio-radius-sm)]" style={{ color: "var(--gio-text)", background: "var(--gio-bg)", borderColor: "var(--gio-border)" }}>
            <Trash2 size={14} />
          </button>
        </div>
      ) : (
        <button onClick={pick} disabled={busy} data-testid={`${testid}-pick`}
          className="w-full aspect-[3/4] flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-[var(--gio-radius)] transition disabled:opacity-50" style={{ borderColor: "var(--gio-border-hi)", color: "var(--gio-muted)" }}>
          <Upload size={20} />
          <span className="text-[11px] font-mono-ui uppercase">{busy ? "Uploading..." : "Choose photo"}</span>
        </button>
      )}
      <input type="file" ref={inputRef} accept="image/*" onChange={handle} className="hidden" data-testid={`${testid}-input`} />
      <div className="text-xs mt-2" style={{ color: "var(--gio-muted)" }}>{hint}</div>
    </div>
  );
}

// ─────────────────── Drawn signature pad ───────────────────
function SignaturePad({ value, onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(!!value);
  const [ready, setReady] = useState(false);

  // Setup canvas with proper devicePixelRatio (layout effect → fires before paint)
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = getComputedStyle(document.documentElement).getPropertyValue("--gio-text").trim();
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = value;
    }
    setReady(true);
  }, []); // intentionally run once on mount

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches?.[0];
    const clientX = t ? t.clientX : e.clientX;
    const clientY = t ? t.clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    if (!ready) return;
    e.preventDefault();
    drawing.current = true;
    last.current = getPos(e);
  };
  const move = (e) => {
    if (!drawing.current || !ready) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    setHasInk(true);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    // Export as PNG data URL — captures black bg + white strokes
    const url = canvasRef.current.toDataURL("image/png");
    onChange(url);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    setHasInk(false);
    onChange("");
  };

  return (
    <div className="space-y-2" data-testid="cf-sigpad-wrap">
      <div className="text-sm font-semibold" style={{ color: "var(--gio-text-2)" }}>Draw your signature *</div>
      <div className="relative">
        <canvas
          ref={canvasRef}
          data-testid="cf-sigpad"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          className="w-full border rounded-[var(--gio-radius)] cursor-crosshair touch-none"
          style={{ height: 180, background: "var(--gio-surface-2)", borderColor: "var(--gio-border)" }}
        />
        {!hasInk && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-sm font-mono-ui uppercase tracking-wider" style={{ color: "var(--gio-muted-2)" }}>
            Sign here with finger or mouse
          </div>
        )}
        <button
          type="button"
          onClick={clear}
          data-testid="cf-sigpad-clear"
          className="absolute top-2 right-2 border px-2 py-1 text-[10px] font-mono-ui uppercase transition inline-flex items-center gap-1 rounded-[var(--gio-radius-sm)]" style={{ background: "var(--gio-bg)", borderColor: "var(--gio-border)", color: "var(--gio-muted)" }}>
          <Eraser size={11} /> Clear
        </button>
      </div>
      <div className="text-xs" style={{ color: "var(--gio-muted)" }}>Your drawn signature will be embedded into the PDF contract Gio downloads.</div>
    </div>
  );
}
